import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { crossBtnGrey, THREE_DOTS_VERTICAL } from '../../assets/hostedassets';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { Button, Modal } from '../_custom';
import { formatDateByFormat, DATE_FORMATS } from '../../utils/dateUtils';
import styles from './HistorySideDrawer.module.scss';

export interface HistoryEntry {
    _id: string;
    userName: string;
    lastUpdatedAt: number;
    isRestored: boolean;
}

export interface HistorySideDrawerProps {
    containerId: string;
    isOpen: boolean;
    isExpanded: boolean;
    title: string;
    loading: boolean;
    historyEntries: HistoryEntry[];
    onClose: () => void;
    onToggleExpand: () => void;
    onSelectEntry: (entry: HistoryEntry) => void;
    onPreviewEntry?: (entry: HistoryEntry) => void;
    dateFormat?: string;
    previewLoading?: boolean;
    selectedEntryId?: string | null;
    onRestoreClick?: (entry: HistoryEntry) => void;
    onRestoreConfirm?: () => void;
}
const CLOSE_ANIMATION_DURATION = 200;

function HistorySideDrawer({
    containerId,
    isOpen,
    isExpanded,
    title,
    loading,
    historyEntries,
    onClose,
    onToggleExpand,
    onSelectEntry,
    onPreviewEntry,
    dateFormat = DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY,
    previewLoading = false,
    selectedEntryId = null,
    onRestoreClick,
    onRestoreConfirm,
}: HistorySideDrawerProps) {
    const [showMenu, setShowMenu] = useState<string | null>(null);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);

    // Handle click outside menu (moved outside useEffect)
    const handleClickOutside = useCallback((e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest(`.${styles.MenuIcon}`) && !target.closest(`.${styles.MenuDropdown}`)) {
            setShowMenu(null);
        }
    }, []);

    // Handle opening and closing animations
    const handleAnimationState = useCallback(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsAnimating(false);
            return;
        }

        if (shouldRender) {
            setIsAnimating(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsAnimating(false);
            }, CLOSE_ANIMATION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    useEffect(() => {
        return handleAnimationState();
    }, [handleAnimationState]);

    // Close menu when clicking outside
    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, handleClickOutside]);

    const handleRowClick = useCallback(
        (entry: HistoryEntry) => {
            onPreviewEntry?.(entry);
        },
        [onPreviewEntry],
    );

    const handleThreeDotsClick = useCallback((e: React.MouseEvent, entryId: string) => {
        e.stopPropagation();
        setShowMenu((prev) => (prev === entryId ? null : entryId));
    }, []);

    const handleRestoreClick = useCallback((e: React.MouseEvent, entry: HistoryEntry) => {
        e.stopPropagation();
        setSelectedEntry(entry);
        setShowRestoreModal(true);
        setShowMenu(null);
    }, []);

    const handleConfirmRestore = useCallback(() => {
        if (selectedEntry) {
            onRestoreConfirm?.(); // Trigger Mixpanel tracking
            onSelectEntry(selectedEntry);
            setShowRestoreModal(false);
            setSelectedEntry(null);
        }
    }, [selectedEntry, onSelectEntry, onRestoreConfirm]);

    const handleCancelRestore = useCallback(() => {
        setShowRestoreModal(false);
        setSelectedEntry(null);
    }, []);

    const renderHistoryEntry = useCallback(
        (entry: HistoryEntry, index: number) => {
            const isLatest = index === 0;
            const isSelected = entry._id === selectedEntryId;

            return (
                <div
                    key={entry._id}
                    className={clsx(styles.HistoryEntry, {
                        [styles.HistoryEntry_Selected]: isSelected,
                    })}
                    onClick={() => handleRowClick(entry)}
                >
                    <div className={styles.CircleIndicator} />
                    <div className={styles.EntryContent}>
                        {isLatest && entry.isRestored ? (
                            <>
                                <div className={styles.CurrentVersionLabel}>Current Version</div>
                                <div className={styles.UserInfo}>Restored, {formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>
                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        ) : isLatest ? (
                            <>
                                <div className={styles.CurrentVersionLabel}>Current Version</div>
                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        ) : entry.isRestored ? (
                            <>
                                <div className={styles.UpdatedAt}>{formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>
                                <div className={styles.UserInfo}>Restored, {formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>

                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        ) : (
                            <>
                                <div className={styles.UpdatedAt}>{formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>
                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        )}
                    </div>
                    {!isLatest && (
                        <div className={styles.MenuContainer}>
                            <img
                                src={THREE_DOTS_VERTICAL}
                                alt="menu"
                                className={styles.MenuIcon}
                                onClick={(e) => handleThreeDotsClick(e, entry._id)}
                            />
                            {showMenu === entry._id && (
                                <div className={styles.MenuDropdown}>
                                    <div className={styles.MenuItem} onClick={(e) => handleRestoreClick(e, entry)}>
                                        Restore
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        },
        [dateFormat, showMenu, handleRowClick, handleThreeDotsClick, handleRestoreClick, selectedEntryId],
    );

    const renderContent = useMemo(() => {
        if (loading) {
            return (
                <div className={styles.LoadingContainer}>
                    <LoadingSpinner color="var(--primary-text-colour)" height="1.875rem" />
                    <div className={styles.LoadingText}>Loading history...</div>
                </div>
            );
        }

        if (!historyEntries?.length) {
            return (
                <div className={styles.EmptyState}>
                    <div className={styles.EmptyText}>No history available</div>
                </div>
            );
        }

        return <div className={styles.HistoryList}>{historyEntries.map(renderHistoryEntry)}</div>;
    }, [loading, historyEntries, renderHistoryEntry]);

    if (!shouldRender) {
        return null;
    }

    const containerElement = document.getElementById(containerId);
    if (!containerElement) {
        return null;
    }

    return createPortal(
        <>
            <div className={clsx(styles.HistorySideDrawer, { [styles.Expanded]: isExpanded, [styles.Closing]: isAnimating })}>
                <div className={styles.ContentWrapper}>
                    <div className={styles.Header}>
                        <div className={styles.Title}>{title}</div>
                        <div className={styles.Actions}>
                            <img src={crossBtnGrey} alt="close" className={styles.CloseIcon} onClick={onClose} />
                        </div>
                    </div>
                    <div className={styles.Content}>{renderContent}</div>
                </div>
            </div>

            {showRestoreModal && selectedEntry && (
                <Modal open={showRestoreModal} onClose={handleCancelRestore} size="flexible">
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.ModalTitle}>Restore this version?</div>
                        <div className={styles.ModalSubtext}>
                            Are you sure you want to restore the version from{' '}
                            {formatDateByFormat(selectedEntry.lastUpdatedAt, DATE_FORMATS.TIME_DATE_FORMAT_H_MM_A_DD_MMMM_YYYY)}?
                        </div>
                        <div className={styles.ModalButtons}>
                            <Button text="Cancel" onClick={handleCancelRestore} variant="purpleinverted" style={{ marginTop: '0' }} />
                            <Button text="Restore" onClick={handleConfirmRestore} variant="primary" style={{ marginTop: '0' }} />
                        </div>
                    </div>
                </Modal>
            )}
        </>,
        containerElement,
    );
}

export default HistorySideDrawer;
